import { useEffect, useState, useContext, useMemo } from "react";
import AuthContext from "../../contexts/auth-context";
import useAxios from "../../hooks/useAxios";
import {
    Box,
    Typography,
    Pagination,
    CircularProgress,
} from "@mui/material";
import { toastify_status, PAGE_SIZE } from "../../components/config/variables";
import Toastify from "../../components/ui/Toastify";
import TableComponent from "../../components/ui/Table";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import FilterModal from "../../components/filter/FilterModal";
import TuneIcon from '@mui/icons-material/Tune';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { motion } from 'framer-motion';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { validateDispensedDrugs } from "../../Utils/validateHttpResponse";
import { useLocation } from "react-router-dom";

const DispensingRecords = () => {
    const { operation } = useAxios();
    const { token } = useContext(AuthContext);
    const location = useLocation()
    const dispensedToday = location.state?.dispensedToday;

    const [filterOpen, setFilterOpen] = useState(false);

    // get todays date yyyy-mm-dd
    const getToday = () => new Date().toISOString().split('T')[0];
    const [fromDate, setFromDate] = useState(dispensedToday ? getToday() : '');
    const [toDate, setTodate] = useState(dispensedToday ? getToday() : '');
    const [patientFilter, setPatientFilter] = useState('');

    const [dispensedByFilter, setDipensedByFilter] = useState('');

    const [pharmacyList, setPharmacyList] = useState([])

    // used to pass to the filters modal to update the filters after delete a spesific filter
    const [newFiltersAfterDelete, setNewFiltersAfterDelete] = useState(undefined)

    const [dispensingRecords, setDispensingRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    // pagination
    const [currentPage, setCurrentPage] = useState(1);

    // Sorting
    const [sortBy, setSortBy] = useState("dispensed_at");
    const [sortDirection, setSortDirection] = useState("asc")

    const handleSort = (columnKey) => {
        if (sortBy === columnKey) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortBy(columnKey);
            setSortDirection("asc");
        }
    };

    // Sort
    const filteredData = useMemo(() => {
        let filtered = [...dispensingRecords];

        filtered.sort((a, b) => {
            const aVal = a[sortBy];
            const bVal = b[sortBy];

            if (!isNaN(aVal) && !isNaN(bVal)) {
                return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
            } else if (aVal && bVal) {
                return sortDirection === "asc"
                    ? aVal.toString().localeCompare(bVal.toString())
                    : bVal.toString().localeCompare(aVal.toString());
            }

            return 0;
        });

        return filtered;
    }, [dispensingRecords, sortBy, sortDirection]);

    // Pagination
    const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
    const indexOfLastRow = currentPage * PAGE_SIZE;
    const indexOfFirstRow = indexOfLastRow - PAGE_SIZE;
    const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (fromDate) {
            params.set('from_date', fromDate);
        }
        if (toDate) {
            params.set('to_date', toDate);
        }
        if (patientFilter) {
            params.set('patient_name', patientFilter);
        }
        if (dispensedByFilter) {
            params.set('dispensed_by', dispensedByFilter);
        }

        operation(
            {
                method: "GET",
                url: `api/pharmacy/dispensing/dispense?${params}`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                if(validateDispensedDrugs(res)) {
                    setDispensingRecords(res || []);
                } else {
                    Toastify({message: "Invalid response for dispensed medicines.", state: toastify_status.ERROR});
                }
                setLoading(false);
            },
            () => {
                setLoading(false);
                Toastify("Failed to load dispensed medicines.", toastify_status.ERROR);
            }
        );
        // eslint-disable-next-line
    }, [fromDate, toDate, patientFilter, dispensedByFilter]);

    const handleChangePage = (_, value) => {
        setCurrentPage(value);
    };

    const tableHeaders = [
        { label: "Generic Name", key: "drug_name" },
        { label: "Patient Name", key: "patient_name" },
        { label: "Dispensed By", key: "dispensed_by_name" },
        { label: "Dispensed At", key: "dispensed_at" },
        { label: "Quantity", key: "quantity_dispensed" },
        { label: "Unit Price", key: "price_per_unit" },
        { label: "Total Cost", key: "total_cost" },
    ]

    const handleApplyFilters = (selected) => {
        setFromDate(selected.fromDate);
        setTodate(selected.toDate);
        setPatientFilter(selected.patient);
        setDipensedByFilter(selected.dispensedBy);
        setCurrentPage(1);
    };

    const deleteSpesificFilter = (filterKey) => {
        const updatedFilters = {
            fromDate: fromDate,
            toDate: toDate,
            patient: patientFilter,
            dispensedBy: dispensedByFilter,
            [filterKey]: ''
        };

        handleApplyFilters(updatedFilters);
        setNewFiltersAfterDelete(updatedFilters);
    };

    const getPharmacyList = () => {
        operation(
            {
                method: "GET",
                url: `/api/user/pharmacyadminprofile/list_names`,
                headers: { Authorization: `token ${token}` },
            },
            (res) => {
                const normalized = res.map(item => ({
                    label: item.pharmacy_admin_name,
                    value: item.pharmacy_admin_name
                }));
                setPharmacyList(normalized || []);
            },
            () => {
                Toastify("Failed to load pharmacy list.", toastify_status.ERROR);
            }
        );
    }

    useEffect(() => {
        getPharmacyList();
        // eslint-disable-next-line
    }, [])

    return (
        <motion.div
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={pageTransition}>
            <Box>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >


                    <Typography sx={{ fontWeight: "500", fontSize: "20px" }}>
                        Dispensed Medicines
                    </Typography>
                </Box>

                <Box sx={{ mb: 2, display: 'flex' }}>
                    {/* Filters Button */}
                    <Box
                        onClick={() => setFilterOpen(true)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            mr: 2,
                            borderRadius: "5px",
                            bgcolor: "primary.main",
                            color: "white",
                            cursor: "pointer",
                            transition: ".2s",
                            "&:hover": { opacity: 0.9 },
                        }}
                    >
                        <TuneIcon fontSize="small" />
                        <Typography fontWeight={600}>All Filters</Typography>
                    </Box>
                    {/* Active Filters */}
                    {[
                        { label: "From Date", value: fromDate, key: "fromDate" },
                        { label: "To Date", value: toDate, key: "toDate" },
                        { label: "Patient", value: patientFilter, key: "patient" },
                        { label: "Dispensed By", value: dispensedByFilter, key: "dispensedBy" },
                    ]
                        .filter(f => f.value)
                        .map(f => (
                            <Box
                                key={f.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 1,
                                    mr: 1,
                                    borderRadius: "10px",
                                    bgcolor: "background.default",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                                }}
                            >
                                <Typography fontWeight={500}>
                                    {f.label}: {f.value}
                                </Typography>

                                <HighlightOffIcon
                                    onClick={() => deleteSpesificFilter(f.key)}
                                    sx={{
                                        cursor: "pointer",
                                        fontSize: 18,
                                        color: "text.secondary",
                                        "&:hover": { color: "error.main" },
                                    }}
                                />
                            </Box>
                        ))}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableComponent
                        tableHead={tableHeaders.map((h) => (
                            <Box
                                key={h.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                }}
                                onClick={() => handleSort(h.key)}
                            >
                                {h.label}
                                {sortBy === h.key && (
                                    sortDirection === 'asc' ? (
                                        <ArrowDropUp fontSize="small" />
                                    ) : (
                                        <ArrowDropDown fontSize="small" />
                                    )
                                )}
                            </Box>
                        ))}
                        tableBody={currentRows}
                        bodyCells={tableHeaders.map((h) => h.key)}
                        bodyCellsCustomColor={{}}
                        bodyCellsCustomSymbol={{ price_per_unit: '$', total_cost: '$' }}
                        customStyle={{
                            tableContainer: {
                                maxHeight: '555px',
                                overflowY: "auto"
                            },
                            headerTableCell: (cell) => {
                                return ({
                                    textAlign: 'center',
                                    border: '1px solid #d9d9d9',
                                    fontSize: '18px',
                                    fontWeight: '500',
                                    color: '#000000c7',
                                    backgroundColor: '#F5F5F5',
                                    padding: '10px 0 5px',
                                    width:
                                        cell.key === 'drug_name' ? '24%' :
                                        cell.key === 'patient_name' ? '16%' : 
                                        cell.key === 'dispensed_by_name' ? '16%' :
                                        cell.key === 'dispensed_at' ? '16%' :
                                        cell.key === 'quantity_dispensed' ? '8%' :
                                        '10%'
                                })
                            },
                            row: {
                                transition: '0.2s',
                                border: '1px solid #d9d9d9',
                                backgroundColor: '#FCFEFE',
                                '&:hover': {
                                    backgroundColor: '#f9f9f9',
                                },
                            },
                            tableCell: {
                                border: '1px solid #d9d9d9',
                                textAlign: 'center',
                                padding: '14px',
                                fontWeight: '500',
                            },
                        }}
                    />
                )}

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={2}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handleChangePage}
                            color="primary"
                            disabled={loading}
                        />
                    </Box>
                )}

                {/* Filter Modal */}
                <FilterModal
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    onApply={handleApplyFilters}
                    newFiltersAfterDelete={newFiltersAfterDelete}
                    initialFilters={{
                        fromDate,
                        toDate,
                        patient: patientFilter,
                        dispensedBy: dispensedByFilter,
                    }}
                    fields={["date", "patient"]}
                    customFields={[
                        {
                            name: 'dispensedBy',
                            label: 'Dsipensed By',
                            options: pharmacyList,
                        },
                    ]}
                />
            </Box>
        </motion.div>

    );
};

export default DispensingRecords;